import React, { useEffect, useRef, useSyncExternalStore } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

import { getUser } from 'helpers/user';
import { getRouterBasename } from 'helpers/config';
import { openPopupWindow } from 'helpers/generic';
import NotificationAudio from './notification_normal.mp3';

let notificationAudio = new Audio(NotificationAudio);

// ---- Estado global a nivel de módulo (sobrevive remounts) ----
let _notices = [];
let _open = false;
let _listeners = new Set();
let _pollInterval = null;
let _cancelToken = null;
let _loading = false;
let _lastCount = null;
let _fabPos = JSON.parse(localStorage.getItem('fabNoticesPos')) || { bottom: 20, right: 20 };

function _playNotificationSound() {
    const promise = notificationAudio.play();
    if ( promise ) {
        promise.catch(() => {
            const handler = () => setTimeout(() => notificationAudio.play(), 3000);
            document.addEventListener('click', handler, { once: true });
        });
    }
}

function _setNotices(notices) {
    const newCount = notices.length;
    if ( _lastCount === null ? newCount > 0 : newCount > _lastCount ) {
        _playNotificationSound();
    }
    _lastCount = newCount;
    _notices = notices;
    _notify();
}

function _setOpen(open) {
    _open = typeof open === 'function' ? open(_open) : open;
    _notify();
}

function _removeNotice(id) {
    _notices = _notices.filter(n => n.id !== id);
    _lastCount = _notices.length;
    _notify();
}

async function _fetchNotices(userId) {
    if ( _loading || !userId ) return;
    _loading = true;

    try {
        _cancelToken = axios.CancelToken.source();
        const response = await axios.get('/api/manager/notices/notifications-list', {
            params: { user_id: userId },
            cancelToken: _cancelToken.token
        });
        _setNotices([...response.data]);
    } catch (error) {
        if ( !axios.isCancel(error) ) console.error(error);
    }

    _loading = false;
}

function _startPolling(userId) {
    if ( _pollInterval ) return;
    _fetchNotices(userId);
    _pollInterval = setInterval(() => _fetchNotices(userId), 60000);
}

function _stopPolling() {
    if ( _pollInterval ) {
        clearInterval(_pollInterval);
        _pollInterval = null;
    }
    if ( _cancelToken ) {
        _cancelToken.cancel();
        _cancelToken = null;
    }
}

function _subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}

let _snapshot = { notices: _notices, open: _open, fabPos: _fabPos };

function _notify() {
    _snapshot = { notices: _notices, open: _open, fabPos: _fabPos };
    _listeners.forEach(fn => fn());
}

function _getSnapshot() {
    return _snapshot;
}

// ---- Styled components ----

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
    70% { box-shadow: 0 0 0 15px rgba(220, 53, 69, 0); }
    100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
`;

const FabButton = styled.button`
    position: fixed;
    z-index: 9999;
    height: 48px;
    padding: 0 18px;
    border-radius: 8px;
    background: var(--bs-danger);
    color: white;
    border: none;
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    box-shadow: 0 4px 16px rgba(220, 53, 69, 0.4);
    animation: ${props => props.$hasNotices ? pulse : 'none'} 2s infinite;
    touch-action: none;
    user-select: none;
    letter-spacing: 0.3px;

    i {
        font-size: 18px;
    }

    &:active {
        cursor: grabbing;
    }
`;

const Panel = styled.div`
    position: fixed;
    width: 380px;
    max-height: calc(100vh - 120px);
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
    z-index: 9998;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform-origin: ${props => props.$above ? 'bottom right' : 'top right'};
    transition: transform 0.2s ease, opacity 0.2s ease;
    transform: ${props => props.$open ? 'scale(1)' : 'scale(0.8)'};
    opacity: ${props => props.$open ? 1 : 0};
    pointer-events: ${props => props.$open ? 'auto' : 'none'};
`;

const PanelHeader = styled.div`
    padding: 16px;
    background: var(--bs-danger);
    color: white;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
`;

const PanelBody = styled.div`
    overflow-y: auto;
    flex: 1;
    max-height: calc(100vh - 200px);
`;

const NoticeItem = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    transition: background 0.15s;

    &:hover {
        background: #f8f9fa;
    }

    &:last-child {
        border-bottom: none;
    }
`;

const NoticeContent = styled.div`
    flex: 1;
    min-width: 0;

    .notice-header {
        color: ${props => props.$color};
        text-transform: uppercase;
        font-weight: 600;
        font-size: 12px;
    }

    .notice-meta {
        font-size: 11px;
        color: var(--bs-secondary);
    }

    .notice-excerpt {
        font-size: 13px;
        color: #333;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const MarkReadButton = styled.button`
    border: none;
    background: none;
    color: ${props => props.$color};
    cursor: pointer;
    padding: 4px;
    font-size: 16px;
    flex-shrink: 0;
    border-radius: 4px;
    transition: all 0.15s;
    opacity: 0.7;

    &:hover {
        opacity: 1;
    }
`;

const EmptyState = styled.div`
    padding: 40px 20px;
    text-align: center;
    color: var(--bs-secondary);
    font-size: 14px;

    i {
        font-size: 40px;
        display: block;
        margin-bottom: 10px;
    }
`;

// ---- Helpers ----

function formatDate(dateStr) {
    if ( !dateStr ) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ---- Componente ----

export default function NoticesNotifications() {
    const userId = getUser()?.id ?? null;
    const { notices, open, fabPos } = useSyncExternalStore(_subscribe, _getSnapshot);
    const fabRef = useRef(null);
    const dragRef = useRef({ dragging: false, startX: 0, startY: 0, moved: false });

    useEffect(() => {
        if ( !userId ) return;
        _startPolling(userId);
    }, [userId]);

    // ---- Drag handlers ----
    const onPointerDown = (e) => {
        dragRef.current = {
            dragging: true,
            startX: e.clientX,
            startY: e.clientY,
            startBottom: _fabPos.bottom,
            startRight: _fabPos.right,
            moved: false,
        };
        e.target.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
        if ( !dragRef.current.dragging ) return;
        const dy = e.clientY - dragRef.current.startY;

        if ( Math.abs(dy) > 5 ) {
            dragRef.current.moved = true;
        }

        const navbar = document.querySelector('.navbar');
        const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 56;
        const maxBottom = window.innerHeight - navbarHeight - 20 - 48;
        const newBottom = Math.max(20, Math.min(maxBottom, dragRef.current.startBottom - dy));

        _fabPos = { ..._fabPos, bottom: newBottom };
        if ( open ) {
            _notify();
        } else if ( fabRef.current ) {
            fabRef.current.style.bottom = newBottom + 'px';
        }
    };

    const onPointerUp = () => {
        const wasDrag = dragRef.current.moved;
        dragRef.current.dragging = false;

        if ( wasDrag ) {
            localStorage.setItem('fabNoticesPos', JSON.stringify(_fabPos));
        } else {
            _setOpen(prev => !prev);
        }
    };

    const markAsRead = async (e, notice) => {
        e.stopPropagation();
        _removeNotice(notice.id);
        await axios.post('/api/manager/notices/set-notified/' + notice.id).catch(() => {});
    };

    const openNotice = async (notice) => {
        openPopupWindow(getRouterBasename() + '/notices/edit/' + notice.id);
        _removeNotice(notice.id);
        await axios.post('/api/manager/notices/set-notified/' + notice.id).catch(() => {});
    };

    if ( !userId || notices.length === 0 ) return null;

    const navbar = document.querySelector('.navbar');
    const navbarH = navbar ? navbar.getBoundingClientRect().height : 56;
    const fabTop = window.innerHeight - fabPos.bottom - 56;
    const showAbove = fabTop > window.innerHeight / 2;
    const panelStyle = showAbove
        ? { bottom: (fabPos.bottom + 70) + 'px', right: fabPos.right + 'px', maxHeight: (window.innerHeight - fabPos.bottom - 70 - navbarH - 20) + 'px' }
        : { top: (fabTop + 70) + 'px', right: fabPos.right + 'px', maxHeight: (window.innerHeight - fabTop - 70 - 20) + 'px' };

    return (
        <>
            <FabButton
                ref={fabRef}
                $hasNotices={notices.length > 0}
                style={{ bottom: fabPos.bottom, right: fabPos.right }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
            >
                <i className="bi bi-bell-fill"></i>
                {notices.length > 999 ? '999+' : notices.length}
            </FabButton>

            <Panel $open={open} $above={showAbove} style={panelStyle}>
                <PanelHeader>
                    <span><i className="bi bi-bell-fill me-2"></i>Avisos ({notices.length})</span>
                    <button
                        style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}
                        onClick={() => _setOpen(false)}
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </PanelHeader>
                <PanelBody>
                    {notices.length === 0 && (
                        <EmptyState>
                            <i className="bi bi-bell-slash"></i>
                            No hay avisos pendientes
                        </EmptyState>
                    )}
                    {notices.map((notice) => {
                        let color = notice.status === 'expired' ? 'var(--bs-danger)' : 'var(--bs-primary)';
                        let excerpt = notice.title ?? '';
                        if ( !excerpt ) excerpt = notice.description ? notice.description.substring(0, 100) + '...' : '';

                        return (
                            <NoticeItem key={notice.id} onClick={() => openNotice(notice)}>
                                <NoticeContent $color={color}>
                                    <div className="notice-header">
                                        {notice.status === 'expired' ? 'Aviso vencido' : 'Nuevo aviso'}
                                        &nbsp;{notice.reference}
                                    </div>
                                    <div className="notice-meta">
                                        {notice.community_name && <>{notice.community_name} &middot; </>}
                                        {formatDate(notice.created_at)}
                                    </div>
                                    <div className="notice-excerpt">{excerpt}</div>
                                </NoticeContent>
                                <MarkReadButton
                                    $color={color}
                                    title="Marcar como visto"
                                    onClick={(e) => markAsRead(e, notice)}
                                >
                                    <i className="bi bi-eye"></i>
                                </MarkReadButton>
                            </NoticeItem>
                        );
                    })}
                </PanelBody>
            </Panel>
        </>
    );
}
